import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AnimateHeight, { Height } from 'react-animate-height';
import { useTranslation } from 'react-i18next';
import { AmlHistoryRequest, AmlStatusEnum } from '@/api';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  investigationHistorySelectors,
  userSelectors,
} from '@/redux/selectors';
import { investigationHistoryActions } from '@/redux/actions';
import { InvestigationHistoryItemData } from '@/redux/models';
import {
  Button,
  HistoryEmpty,
  InvestigationHistoryTableItem,
  Loader,
  SectionBox,
} from '@/components';
import { navigationPages } from '@/config';
import { getFormattedDate } from '@/helpers';
import { TableTools } from './components';
import styles from './InvestigationHistory.module.sass';

const initialFilters = {
  createFrom: '',
  createTo: '',
  status: '',
  asset: '',
};

/**
 * Note: `createTo` should add 1 day because of backend filtering logic
 */
const getFilledFilters = (filterList: Record<string, any>) => {
  const filters = {
    ...(!!filterList.asset ? { asset: filterList.asset } : {}),
    ...(!!filterList.status ? { status: filterList.status } : {}),
    ...(!!filterList.createFrom
      ? { createFrom: getFormattedDate(filterList.createFrom, 'YYYY-MM-DD') }
      : {}),
    ...(!!filterList.createTo
      ? {
          createTo: getFormattedDate(
            filterList.createTo.add(1, 'days'),
            'YYYY-MM-DD',
          ),
        }
      : {}),
  };

  return Object.keys(filters).length ? { filter: filters } : {};
};

interface InvestigationHistoryProps {}

const InvestigationHistory: FC<InvestigationHistoryProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [queryHash, setQueryhash] = useState('');
  const [loaderHeight, setLoaderHeight] = useState<Height>(0);
  const [searchFilters, setSearchFilters] = useState<Record<string, string>>({
    ...initialFilters,
  });
  const { loading, error, data } = useAppSelector(
    investigationHistorySelectors.getInvestigationHistorySlice,
  );
  const isEmailConfirmed = useAppSelector(userSelectors.checkIfEmailConfirmed);
  const pageTitle = t('translation.requests_history_title');
  const isShowMoreAvailable = !!data && data.totalCount - data.items.length > 0;
  const isEmpty = !data || data.items.length === 0;

  useEffect(() => {
    // fetch data on the first entrance
    if (!data) {
      dispatch(investigationHistoryActions.fetchInvestigationHistory({}));
    }
  }, [data]);

  useEffect(() => {
    setLoaderHeight(loading ? 'auto' : 0);
  }, [loading]);

  const showMoreHandler = useCallback(() => {
    if (data) {
      if (data.totalCount - data.items.length > 0) {
        const currentPage = Math.ceil(data.items.length / data.pageLimit);

        const payload: AmlHistoryRequest = {
          // backend counts pages from 0
          page: currentPage,
        };

        dispatch(
          investigationHistoryActions.fetchInvestigationHistory(payload),
        );
      }
    }
  }, [dispatch, data]);

  const detailsHandler = useCallback(
    (item: InvestigationHistoryItemData) => {
      if (item.status !== AmlStatusEnum.PENDING && item.connections) {
        navigate(navigationPages.INVESTIGATION_DETAILS.path, {
          state: { extendedItem: item },
        });
      }
    },
    [navigate],
  );

  const searchHandler = useCallback(
    (filters: any = null) => {
      const payload: AmlHistoryRequest = {
        queryHash,
        ...getFilledFilters(filters || searchFilters),
      };

      dispatch(investigationHistoryActions.fetchInvestigationHistory(payload));
    },
    [dispatch, queryHash, searchFilters],
  );

  const queryChangeHandler = useCallback(
    (newValue: string) => {
      setQueryhash(newValue);

      if (newValue.length === 0) {
        searchHandler(searchFilters);
      }
    },
    [searchFilters],
  );

  const filtersChanged = (key: string, value: any) => {
    setSearchFilters((prev) => {
      const filters = { ...prev };
      filters[key] = value;
      return filters;
    });
  };

  const onFilterApply = () => {
    searchHandler();
  };

  const onFilterClear = () => {
    const filters = { ...initialFilters };
    setSearchFilters(() => filters);
    searchHandler(filters);
  };

  return (
    <SectionBox>
      {{
        title: pageTitle,
        actions: isEmailConfirmed && (
          <TableTools
            loading={loading}
            onSearchChangeHandler={(newVal) => queryChangeHandler(newVal)}
            onSearchHandler={searchHandler}
            term={queryHash}
            filters={searchFilters}
            onFiltersChangeHandler={filtersChanged}
            onApplyHandler={onFilterApply}
            onClearHandler={onFilterClear}
          />
        ),
        body: (
          <>
            <AnimateHeight
              className={styles.loader}
              duration={100}
              height={loaderHeight}
            >
              <Loader />
            </AnimateHeight>

            {isEmpty && (
              <HistoryEmpty
                title={t('translation.nomore_empty_string')}
                desc={t('translation.amlhistory_empty_string')}
              />
            )}

            {!isEmpty && (
              <>
                <div
                  className={`${styles.table} ${
                    loading ? styles.table_blur : ''
                  }`}
                >
                  <div className={styles.table__body}>
                    {data.items.map((item) => (
                      <InvestigationHistoryTableItem
                        key={item.uuid}
                        item={item}
                        loading={loading}
                        onDetails={detailsHandler}
                      />
                    ))}
                  </div>
                </div>

                {isShowMoreAvailable && (
                  <div className={styles.show}>
                    <Button
                      size="m"
                      onClick={showMoreHandler}
                      disabled={loading}
                      nowrap
                    >
                      {t('translation.next_page_btn')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </>
        ),
      }}
    </SectionBox>
  );
};

export default InvestigationHistory;
